import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Save, Salad, Dumbbell, CheckCircle2, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import {
  useAppointment,
  useCreateAppointment,
  useUpdateAppointment,
  useDeleteAppointment,
  useSetAppointmentStatus,
  usePatients,
  useAthletes,
} from '../lib/queries';
import { ApiError } from '../lib/api';
import type {
  AppointmentType,
  AppointmentStatus,
  AppointmentWithPatient,
  CreateAppointmentBody,
  UpdateAppointmentBody,
} from '../lib/api';

export type EditorMode = 'create' | 'edit' | 'view';

interface Props {
  mode: EditorMode;
  appointmentId: string | null;
  prefillStart?: string;            // datetime-local "YYYY-MM-DDTHH:MM"
  proKind: 'nutri' | 'coach' | 'both';
  /** Filtra paziente lookup per ruolo attivo (se proKind='both'). Default 'all'. */
  activeRoleFilter?: 'nutri' | 'coach' | 'all';
  onClose: () => void;
}

const TYPE_OPTIONS: Array<{ value: AppointmentType; label: string }> = [
  { value: 'visita', label: 'Visita' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'sessione', label: 'Sessione' },
  { value: 'misurazione', label: 'Misurazione' },
  { value: 'consulenza', label: 'Consulenza' },
  { value: 'altro', label: 'Altro' },
];

const LOCATION_PRESETS = ['Studio', 'Online', 'Palestra', 'A domicilio'];

const DURATION_PRESETS = [15, 30, 45, 60, 75, 90, 120];

interface FormState {
  patient_id: string;
  pro_role: 'nutri' | 'coach';
  type: AppointmentType;
  start_at: string;          // datetime-local
  duration_min: number;
  title: string;
  location: string;
  notes: string;
  status: AppointmentStatus; // visualizzato in edit
}

function emptyForm(prefillStart: string | undefined, defaultRole: 'nutri' | 'coach'): FormState {
  return {
    patient_id: '',
    pro_role: defaultRole,
    type: 'visita',
    start_at: prefillStart || '',
    duration_min: 60,
    title: '',
    location: '',
    notes: '',
    status: 'scheduled',
  };
}

/**
 * Converte ISO UTC → datetime-local "YYYY-MM-DDTHH:MM" nella timezone locale.
 */
function isoUtcToLocalInput(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

/**
 * datetime-local string → ISO UTC string (Z suffix). Lascia parsing al backend
 * (che accetta sia "YYYY-MM-DDTHH:MM" sia ISO). Qui formattiamo come ISO UTC
 * effettivo per consistenza.
 */
function localInputToIsoUtc(local: string): string | null {
  if (!local || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  // new Date(local) interpreta come local time → produce ISO UTC via toISOString
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function appointmentToForm(a: AppointmentWithPatient): FormState {
  return {
    patient_id: a.patient_id,
    pro_role: a.pro_role,
    type: a.type,
    start_at: isoUtcToLocalInput(a.start_at),
    duration_min: a.duration_min,
    title: a.title || '',
    location: a.location || '',
    notes: a.notes || '',
    status: a.status,
  };
}

export function AppointmentEditor({
  mode: initialMode,
  appointmentId,
  prefillStart,
  proKind,
  activeRoleFilter,
  onClose,
}: Props) {
  const defaultRole: 'nutri' | 'coach' = (() => {
    if (proKind === 'coach') return 'coach';
    if (proKind === 'nutri') return 'nutri';
    // both → usa il filtro attivo se diverso da 'all', altrimenti 'nutri'
    if (activeRoleFilter === 'coach') return 'coach';
    return 'nutri';
  })();

  const [mode] = useState<EditorMode>(initialMode);
  const [form, setForm] = useState<FormState>(() => emptyForm(prefillStart, defaultRole));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<'delete' | 'no-show' | null>(null);
  const [loadedAppt, setLoadedAppt] = useState<AppointmentWithPatient | null>(null);

  const isEditOrView = mode === 'edit' || mode === 'view';
  const isView = mode === 'view';

  // Load existing appointment when edit/view
  const apptQuery = useAppointment(isEditOrView ? appointmentId : null);

  // Patient autocomplete: nutri uses /api/pro/patients, coach uses /api/pro/coach/athletes
  // Per pro_kind='both' usiamo entrambe e mergiamo: filtriamo client-side per pro_role del form.
  const showNutriPatients = proKind === 'nutri' || proKind === 'both';
  const showCoachAthletes = proKind === 'coach' || proKind === 'both';
  const patientsQ = usePatients(); // ok always for nutri+both; per coach unused (returns empty if not nutri)
  const athletesQ = useAthletes(showCoachAthletes);

  // Hydrate form when appointment loads (edit/view)
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && apptQuery.data?.appointment) {
      const a = apptQuery.data.appointment;
      setForm(appointmentToForm(a));
      setLoadedAppt(a);
    }
  }, [mode, apptQuery.data]);

  // Lock body scroll on mount
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC chiude
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Mutations
  const createMut = useCreateAppointment();
  const updateMut = useUpdateAppointment();
  const deleteMut = useDeleteAppointment();
  const statusMut = useSetAppointmentStatus();

  // Patient options (filtered by pro_role)
  const patientOptions: Array<{ id: string; name: string; human_id: string | null; pro_role: 'nutri' | 'coach' }> = useMemo(() => {
    const out: Array<{ id: string; name: string; human_id: string | null; pro_role: 'nutri' | 'coach' }> = [];
    if (form.pro_role === 'nutri' && showNutriPatients && patientsQ.data) {
      for (const p of patientsQ.data) {
        out.push({ id: p.id, name: p.name, human_id: p.human_id, pro_role: 'nutri' });
      }
    }
    if (form.pro_role === 'coach' && showCoachAthletes && athletesQ.data) {
      for (const a of athletesQ.data) {
        out.push({ id: a.id, name: a.name, human_id: null, pro_role: 'coach' });
      }
    }
    // Sort by name
    out.sort((a, b) => a.name.localeCompare(b.name, 'it-IT'));
    return out;
  }, [form.pro_role, patientsQ.data, athletesQ.data, showNutriPatients, showCoachAthletes]);

  // Submit handler — wrap async in IIFE per evitare async-in-sync onClick
  const handleSubmit = () => {
    setSubmitError(null);

    // Validation client-side
    if (!form.patient_id) {
      setSubmitError('Seleziona un paziente');
      return;
    }
    if (!form.start_at) {
      setSubmitError('Specifica data e ora');
      return;
    }
    const isoUtc = localInputToIsoUtc(form.start_at);
    if (!isoUtc) {
      setSubmitError('Data/ora non valida');
      return;
    }
    if (!Number.isInteger(form.duration_min) || form.duration_min < 15 || form.duration_min > 480) {
      setSubmitError('Durata deve essere tra 15 e 480 minuti');
      return;
    }

    void (async () => {
      try {
        if (mode === 'create') {
          const body: CreateAppointmentBody = {
            patient_id: form.patient_id,
            pro_role: form.pro_role,
            type: form.type,
            start_at: isoUtc,
            duration_min: form.duration_min,
            title: form.title.trim() || null,
            location: form.location.trim() || null,
            notes: form.notes.trim() || null,
          };
          await createMut.mutateAsync(body);
        } else if (mode === 'edit' && appointmentId) {
          const body: UpdateAppointmentBody = {
            type: form.type,
            start_at: isoUtc,
            duration_min: form.duration_min,
            title: form.title.trim() || null,
            location: form.location.trim() || null,
            notes: form.notes.trim() || null,
          };
          // Se il pro è 'both', include pro_role (potrebbe essere cambiato)
          if (proKind === 'both' && loadedAppt && form.pro_role !== loadedAppt.pro_role) {
            body.pro_role = form.pro_role;
          }
          await updateMut.mutateAsync({ id: appointmentId, body });
        }
        onClose();
      } catch (e) {
        const msg = e instanceof ApiError
          ? `${e.message}`
          : e instanceof Error ? e.message : String(e);
        setSubmitError(msg);
      }
    })();
  };

  const handleStatus = (newStatus: AppointmentStatus) => {
    if (!appointmentId) return;
    setSubmitError(null);
    void (async () => {
      try {
        await statusMut.mutateAsync({ id: appointmentId, status: newStatus });
        onClose();
      } catch (e) {
        const msg = e instanceof ApiError
          ? `${e.message}`
          : e instanceof Error ? e.message : String(e);
        setSubmitError(msg);
      }
    })();
  };

  const handleDelete = () => {
    if (!appointmentId) return;
    setSubmitError(null);
    void (async () => {
      try {
        await deleteMut.mutateAsync(appointmentId);
        onClose();
      } catch (e) {
        const msg = e instanceof ApiError
          ? `${e.message}`
          : e instanceof Error ? e.message : String(e);
        setSubmitError(msg);
      }
    })();
  };

  const headerTitle = mode === 'create'
    ? 'Nuovo appuntamento'
    : mode === 'edit'
    ? 'Modifica appuntamento'
    : 'Dettaglio appuntamento';

  const isLoadingAppt = isEditOrView && apptQuery.isLoading;
  const isLoadingPatients = (form.pro_role === 'nutri' && showNutriPatients && patientsQ.isLoading)
    || (form.pro_role === 'coach' && showCoachAthletes && athletesQ.isLoading);

  const isMutating = createMut.isPending || updateMut.isPending || deleteMut.isPending || statusMut.isPending;

  return (
    <div
      style={modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal
      aria-labelledby="appt-editor-title"
    >
      <div
        style={modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={modalHeader}>
          <h2 id="appt-editor-title" style={headerTitleStyle}>
            {headerTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            style={closeBtnStyle}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div style={modalBody}>
          {isLoadingAppt && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--mute)' }}>
              Caricamento…
            </div>
          )}

          {!isLoadingAppt && (
            <>
              {/* Status badge in edit/view */}
              {isEditOrView && loadedAppt && (
                <StatusBadge status={form.status} proRole={form.pro_role} />
              )}

              {/* Confirm delete inline */}
              {confirmDelete === 'delete' && (
                <ConfirmInline
                  message="Annulla questo appuntamento? Verrà segnato come 'canceled'."
                  onConfirm={() => {
                    setConfirmDelete(null);
                    handleDelete();
                  }}
                  onCancel={() => setConfirmDelete(null)}
                  variant="danger"
                />
              )}
              {confirmDelete === 'no-show' && (
                <ConfirmInline
                  message="Segnare l'appuntamento come 'no-show' (paziente non presentato)?"
                  onConfirm={() => {
                    setConfirmDelete(null);
                    handleStatus('no-show');
                  }}
                  onCancel={() => setConfirmDelete(null)}
                  variant="warning"
                />
              )}

              {/* Form (disabled in view mode) */}
              <fieldset
                disabled={isView}
                style={{
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  opacity: isView ? 0.85 : 1,
                }}
              >
                {/* Pro role (solo se 'both') */}
                {proKind === 'both' && (
                  <FieldRow label="Come">
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <RoleToggle
                        active={form.pro_role === 'nutri'}
                        onClick={() => setForm((s) => ({ ...s, pro_role: 'nutri', patient_id: '' }))}
                        role="nutri"
                      />
                      <RoleToggle
                        active={form.pro_role === 'coach'}
                        onClick={() => setForm((s) => ({ ...s, pro_role: 'coach', patient_id: '' }))}
                        role="coach"
                      />
                    </div>
                  </FieldRow>
                )}

                {/* Patient select */}
                <FieldRow label="Paziente" required>
                  {isLoadingPatients ? (
                    <div style={{ color: 'var(--mute)', fontSize: 13 }}>Caricamento elenco…</div>
                  ) : (
                    <PatientSelect
                      value={form.patient_id}
                      onChange={(id) => setForm((s) => ({ ...s, patient_id: id }))}
                      options={patientOptions}
                      lockedPatientName={isEditOrView && loadedAppt ? loadedAppt.patient_name : null}
                      disabled={isEditOrView}
                    />
                  )}
                </FieldRow>

                {/* Type */}
                <FieldRow label="Tipo">
                  <select
                    value={form.type}
                    onChange={(e) => setForm((s) => ({ ...s, type: e.target.value as AppointmentType }))}
                    style={selectStyle}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FieldRow>

                {/* Datetime */}
                <FieldRow label="Data e ora" required>
                  <input
                    type="datetime-local"
                    value={form.start_at}
                    onChange={(e) => setForm((s) => ({ ...s, start_at: e.target.value }))}
                    style={inputStyle}
                  />
                </FieldRow>

                {/* Duration */}
                <FieldRow label="Durata (min)" required>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      min={15}
                      max={480}
                      step={5}
                      value={form.duration_min}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!isNaN(n)) setForm((s) => ({ ...s, duration_min: n }));
                      }}
                      style={{ ...inputStyle, width: 100 }}
                    />
                    <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                      {DURATION_PRESETS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          disabled={isView}
                          onClick={() => setForm((s) => ({ ...s, duration_min: p }))}
                          style={chipBtnStyle(form.duration_min === p)}
                        >
                          {p}'
                        </button>
                      ))}
                    </div>
                  </div>
                </FieldRow>

                {/* Title (optional) */}
                <FieldRow label="Titolo (opzionale)">
                  <input
                    type="text"
                    placeholder="Es. Visita di controllo trimestrale"
                    maxLength={200}
                    value={form.title}
                    onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                    style={inputStyle}
                  />
                </FieldRow>

                {/* Location with presets */}
                <FieldRow label="Luogo">
                  <div>
                    <input
                      type="text"
                      placeholder="Studio, online, ..."
                      maxLength={200}
                      value={form.location}
                      onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                      style={inputStyle}
                    />
                    {!isView && (
                      <div style={{ display: 'inline-flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                        {LOCATION_PRESETS.map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setForm((s) => ({ ...s, location: p }))}
                            style={chipBtnStyle(form.location === p)}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </FieldRow>

                {/* Notes */}
                <FieldRow label="Note">
                  <textarea
                    rows={3}
                    placeholder="Annotazioni, agenda, follow-up necessari..."
                    maxLength={2000}
                    value={form.notes}
                    onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 70, fontFamily: 'var(--sans)' }}
                  />
                </FieldRow>
              </fieldset>

              {/* Error display */}
              {submitError && (
                <div
                  role="alert"
                  style={{
                    marginTop: 14,
                    padding: 10,
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: 'var(--red)',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{submitError}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={modalFooter}>
          {/* Status transition buttons (edit mode con appuntamento esistente) */}
          {(mode === 'edit' || mode === 'view') && loadedAppt && form.status !== 'canceled' && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
              {form.status !== 'completed' && (
                <button
                  type="button"
                  onClick={() => handleStatus('completed')}
                  disabled={isMutating}
                  style={statusBtnStyle('complete')}
                  title="Segna come completato"
                >
                  <CheckCircle2 size={14} strokeWidth={2} /> Completa
                </button>
              )}
              {form.status !== 'no-show' && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete('no-show')}
                  disabled={isMutating}
                  style={statusBtnStyle('warning')}
                  title="Segna come no-show"
                >
                  <AlertCircle size={14} strokeWidth={2} /> No-show
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmDelete('delete')}
                disabled={isMutating}
                style={statusBtnStyle('danger')}
                title="Annulla appuntamento"
              >
                <Trash2 size={14} strokeWidth={2} /> Annulla
              </button>
            </div>
          )}

          {/* Re-open if canceled (toggle to view-only edit) */}
          {mode === 'view' && loadedAppt && loadedAppt.status === 'canceled' && (
            <button
              type="button"
              onClick={() => handleStatus('scheduled')}
              disabled={isMutating}
              style={statusBtnStyle('complete')}
              title="Riapri appuntamento"
            >
              <XCircle size={14} strokeWidth={2} /> Riapri
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            style={secondaryBtnStyle}
          >
            {isView ? 'Chiudi' : 'Annulla'}
          </button>

          {!isView && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isMutating}
              style={primaryBtnStyle(isMutating)}
            >
              <Save size={14} strokeWidth={2} />
              {isMutating ? 'Salvataggio…' : (mode === 'create' ? 'Crea' : 'Salva')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FieldRow({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontFamily: 'var(--mono)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--red)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function PatientSelect({
  value,
  onChange,
  options,
  lockedPatientName,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  options: Array<{ id: string; name: string; human_id: string | null }>;
  lockedPatientName: string | null;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find selected option (per display name)
  const selected = options.find((o) => o.id === value);

  // Display value: in disabled (edit/view) → locked patient name. Altrimenti query oppure selected name
  const displayValue = (() => {
    if (disabled && lockedPatientName) return lockedPatientName;
    if (open) return query;
    if (selected) return `${selected.name}${selected.human_id ? ` · ${selected.human_id}` : ''}`;
    return query;
  })();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 20);
    return options
      .filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          (o.human_id?.toLowerCase().includes(q) ?? false)
      )
      .slice(0, 30);
  }, [options, query]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => !disabled && setOpen(true)}
        onBlur={() => {
          // Delay per consentire click su dropdown item
          setTimeout(() => setOpen(false), 150);
        }}
        placeholder="Cerca paziente per nome o ID…"
        style={inputStyle}
        disabled={disabled}
      />
      {open && !disabled && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            maxHeight: 220,
            overflowY: 'auto',
            zIndex: 10,
            boxShadow: '0 6px 24px rgba(11, 15, 12, 0.08)',
          }}
        >
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(e) => {
                // onMouseDown ↑↑ onBlur — set value prima del blur close
                e.preventDefault();
                onChange(o.id);
                setQuery('');
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '8px 12px',
                fontSize: 13,
                color: 'var(--ink)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-2)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <span style={{ fontWeight: 500 }}>{o.name}</span>
              {o.human_id && (
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: 'var(--mono)',
                    color: 'var(--mute)',
                  }}
                >
                  {o.human_id}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !disabled && filtered.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: '10px 12px',
            zIndex: 10,
            color: 'var(--mute)',
            fontSize: 13,
            boxShadow: '0 6px 24px rgba(11, 15, 12, 0.08)',
          }}
        >
          Nessun paziente trovato.
        </div>
      )}
    </div>
  );
}

function RoleToggle({
  active,
  onClick,
  role,
}: {
  active: boolean;
  onClick: () => void;
  role: 'nutri' | 'coach';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="radio"
      aria-checked={active}
      style={{
        background: active ? 'var(--ink)' : 'var(--surface)',
        color: active ? '#fff' : 'var(--ink-3)',
        border: active ? '1px solid var(--ink)' : '1px solid var(--line)',
        padding: '7px 14px',
        fontSize: 13,
        fontWeight: 600,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
      }}
    >
      {role === 'nutri' ? <Salad size={13} strokeWidth={2} /> : <Dumbbell size={13} strokeWidth={2} />}
      {role === 'nutri' ? 'Nutri' : 'Coach'}
    </button>
  );
}

function StatusBadge({ status, proRole }: { status: AppointmentStatus; proRole: 'nutri' | 'coach' }) {
  const config: Record<AppointmentStatus, { label: string; bg: string; ink: string }> = {
    scheduled: {
      label: 'Programmato',
      bg: proRole === 'coach' ? 'var(--orange-tint)' : 'var(--green-tint)',
      ink: proRole === 'coach' ? 'var(--orange-deep)' : 'var(--green-deep)',
    },
    completed: { label: 'Completato', bg: 'var(--surface-2)', ink: 'var(--mute)' },
    canceled: { label: 'Annullato', bg: '#fef2f2', ink: 'var(--red)' },
    'no-show': { label: 'No-show', bg: '#fef3c7', ink: '#a16207' },
  };
  const c = config[status];
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: c.bg,
        color: c.ink,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        fontFamily: 'var(--mono)',
        marginBottom: 6,
        alignSelf: 'flex-start',
      }}
    >
      {c.label}
    </div>
  );
}

function ConfirmInline({
  message,
  onConfirm,
  onCancel,
  variant,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant: 'danger' | 'warning';
}) {
  const palette = variant === 'danger'
    ? { bg: '#fef2f2', border: '#fecaca', ink: 'var(--red)' }
    : { bg: '#fef3c7', border: '#fde68a', ink: '#a16207' };
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
      role="alertdialog"
    >
      <div style={{ color: palette.ink, fontSize: 13, fontWeight: 500 }}>{message}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={secondaryBtnStyle}>
          Annulla
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={{
            background: palette.ink,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Conferma
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(11, 15, 12, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: 16,
};

const modalContent: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 16,
  boxShadow: '0 25px 70px rgba(11, 15, 12, 0.25)',
  width: '100%',
  maxWidth: 520,
  maxHeight: '94vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const modalHeader: React.CSSProperties = {
  padding: '18px 22px',
  borderBottom: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const modalBody: React.CSSProperties = {
  padding: '18px 22px',
  overflowY: 'auto',
  flex: 1,
};

const modalFooter: React.CSSProperties = {
  padding: '14px 22px',
  borderTop: '1px solid var(--line)',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};

const headerTitleStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--display)',
  fontSize: 20,
  fontWeight: 700,
  color: 'var(--ink)',
  letterSpacing: -0.5,
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--mute)',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  outline: 'none',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'auto',
};

const secondaryBtnStyle: React.CSSProperties = {
  background: 'var(--surface)',
  color: 'var(--ink-3)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--mute-2)' : 'var(--ink)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
}

function statusBtnStyle(variant: 'complete' | 'warning' | 'danger'): React.CSSProperties {
  const palettes = {
    complete: { bg: 'var(--green-tint)', border: 'var(--green)', ink: 'var(--green-deep)' },
    warning: { bg: '#fef3c7', border: '#fde68a', ink: '#a16207' },
    danger: { bg: '#fef2f2', border: '#fecaca', ink: 'var(--red)' },
  };
  const p = palettes[variant];
  return {
    background: p.bg,
    color: p.ink,
    border: `1px solid ${p.border}`,
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  };
}

function chipBtnStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? 'var(--ink)' : 'var(--surface-2)',
    color: active ? '#fff' : 'var(--ink-3)',
    border: active ? '1px solid var(--ink)' : '1px solid var(--line)',
    padding: '5px 10px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 999,
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
  };
}
